import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    SettingsPanel,
    SettingsRow,
    SettingsSection,
} from '@apps/web/components/settings/settings_primitives';
import { SmsCancellationReplyCard } from '@apps/web/components/settings/sms_cancellation_reply_card';
import {
    createDefaultWeeklyScheduleRule,
    isWeeklyScheduleRuleValid,
    type WeeklyScheduleRuleFormValue,
    WeeklyScheduleRulesEditor,
} from '@apps/web/components/settings/weekly_schedule_dialog';
import { getTemplateVariablePreviewValuesForOrganization } from '@apps/web/components/shared/messages/template_variable_metadata';
import {
    AvailableVariables,
    VariableTextEditor,
} from '@apps/web/components/shared/messages/variable_text_editor';
import { Button, type ButtonStatus } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { Switch } from '@apps/web/components/ui/switch';
import {
    SettingsPageSection,
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getTemplateVariablesForFlow } from '@apps/web/lib/template_variables';
import {
    useCompletionTemplates,
    useOrganization,
    useTemplateVariableManifest,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/settings/cancellations')({
    component: CancellationsPage,
});

const DEFAULT_COMPLETION_CANCELLED_TEMPLATE =
    'Hi {caregiver_first_name}, the following shifts have been cancelled: {shift_list}';

function CancellationsPage() {
    const { organizationId, isLoading, gates } = useSettingsGates();
    if (isLoading) {
        return null;
    }
    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }
    if (organizationId === null) {
        return null;
    }

    return (
        <SettingsPageShell
            title="Cancellations"
            description="What happens when a caregiver calls out of a shift, and the messages Phoebe sends when shifts are cancelled."
        >
            <SettingsPageSections>
                <CaregiverCalloutsSection organizationId={organizationId} />
                <AgencyCancellationNoticeSection
                    organizationId={organizationId}
                />
            </SettingsPageSections>
        </SettingsPageShell>
    );
}

type CancellationChannel = 'sms' | 'voice';
type CancellationPolicyApi =
    components['schemas']['CancellationPolicySettingsUpdate'];
type CancellationPolicyWindow = components['schemas']['RecurringWeeklyWindow'];
type CancellationPolicyDraft = {
    readonly enabled_channels: ReadonlyArray<CancellationChannel>;
    readonly look_ahead_hours: CancellationPolicyApi['look_ahead_hours'];
    readonly acceptance_windows: ReadonlyArray<CancellationPolicyWindow>;
};

function isCancellationPolicy(
    value: unknown,
): value is CancellationPolicyDraft {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    if (
        !('enabled_channels' in value) ||
        !('look_ahead_hours' in value) ||
        !('acceptance_windows' in value)
    ) {
        return false;
    }
    const channels = value.enabled_channels;
    const windows = value.acceptance_windows;
    return (
        Array.isArray(channels) &&
        channels.every((channel) => channel === 'sms' || channel === 'voice') &&
        typeof value.look_ahead_hours === 'number' &&
        Number.isFinite(value.look_ahead_hours) &&
        value.look_ahead_hours >= 0 &&
        Array.isArray(windows)
    );
}

function readCancellationPolicy(settings: unknown): CancellationPolicyDraft {
    if (
        typeof settings === 'object' &&
        settings !== null &&
        'cancellation_policy' in settings &&
        isCancellationPolicy(settings.cancellation_policy)
    ) {
        return settings.cancellation_policy;
    }
    return {
        enabled_channels: [],
        look_ahead_hours: 0,
        acceptance_windows: [],
    };
}

function toScheduleRule(
    window: CancellationPolicyWindow,
    index: number,
): WeeklyScheduleRuleFormValue {
    return {
        id: `cancellation-window-${index}`,
        daysOfWeek: window.days_of_week.filter(
            (day): day is WeeklyScheduleRuleFormValue['daysOfWeek'][number] =>
                [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ].includes(day),
        ),
        allDay: window.all_day,
        startTime: window.start_time ?? '09:00',
        endTime: window.end_time ?? '17:00',
    };
}

function fromScheduleRule(
    rule: WeeklyScheduleRuleFormValue,
): CancellationPolicyWindow {
    return {
        days_of_week: [...rule.daysOfWeek],
        all_day: rule.allDay,
        start_time: rule.allDay ? null : rule.startTime,
        end_time: rule.allDay ? null : rule.endTime,
    };
}

function CaregiverCalloutsSection({
    organizationId,
}: {
    readonly organizationId: string;
}) {
    const { data: organization, mutate: mutateOrganization } = useOrganization(
        organizationId,
        { reason: 'settings-cancellations-callouts' },
    );
    const [isUpdating, setIsUpdating] = useState(false);
    const [dirty, setDirty] = useState(false);
    const policy = useMemo(
        () => readCancellationPolicy(organization?.general_settings),
        [organization?.general_settings],
    );
    const [draft, setDraft] = useState<CancellationPolicyDraft>(policy);
    const [hoursText, setHoursText] = useState(String(policy.look_ahead_hours));
    const [rules, setRules] = useState<
        ReadonlyArray<WeeklyScheduleRuleFormValue>
    >(policy.acceptance_windows.map(toScheduleRule));
    const alwaysTransferPhoneCallouts =
        organization?.general_settings
            ?.inbound_voice_shift_cancellation_always_transfer ?? false;
    useEffect(() => {
        if (dirty) {
            return;
        }
        setDraft(policy);
        setHoursText(String(policy.look_ahead_hours));
        setRules(policy.acceptance_windows.map(toScheduleRule));
    }, [dirty, policy]);

    const smsEnabled = draft.enabled_channels.includes('sms');
    const voiceEnabled = draft.enabled_channels.includes('voice');
    const hasInvalidWindow = rules.some(
        (rule) => !isWeeklyScheduleRuleValid(rule),
    );

    const savePolicy = async (policyDraft: CancellationPolicyDraft = draft) => {
        const trimmedHours = hoursText.trim();
        const hours = Number(trimmedHours);
        if (trimmedHours === '' || !Number.isFinite(hours) || hours < 0) {
            toast.error('Enter a non-negative number of hours', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return;
        }
        if (hasInvalidWindow) {
            toast.error('Complete or remove each acceptance window', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return;
        }
        setIsUpdating(true);
        try {
            const body = {
                cancellation_policy: {
                    enabled_channels: [...policyDraft.enabled_channels],
                    look_ahead_hours: hours,
                    acceptance_windows: rules.map(fromScheduleRule),
                },
            };
            await getApiClient().put({
                url: '/organizations/{organization_id}/general-settings',
                pathParams: { organization_id: organizationId },
                body,
            });
            await mutateOrganization();
            setDirty(false);
            toast.success('Cancellation policy saved', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return true;
        } catch {
            toast.error('Failed to save cancellation policy', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return false;
        } finally {
            setIsUpdating(false);
        }
    };

    const setChannels = async (
        channel: CancellationChannel,
        checked: boolean,
    ) => {
        const previousDraft = draft;
        const wasDirty = dirty;
        const enabledChannels = checked
            ? [...new Set([...draft.enabled_channels, channel])]
            : draft.enabled_channels.filter((value) => value !== channel);
        const nextDraft = {
            ...draft,
            enabled_channels: enabledChannels,
        };
        setDraft(nextDraft);
        if (!(await savePolicy(nextDraft))) {
            setDraft(previousDraft);
            setDirty(wasDirty);
        }
    };

    const saveAlwaysTransfer = async (enabled: boolean) => {
        setIsUpdating(true);
        try {
            await getApiClient().put({
                url: '/organizations/{organization_id}/general-settings',
                pathParams: { organization_id: organizationId },
                body: {
                    inbound_voice_shift_cancellation_always_transfer: enabled,
                },
            });
            await mutateOrganization();
            toast.success(
                enabled
                    ? 'Phone call-outs will be connected to a scheduler'
                    : 'Phone call-outs will end with Phoebe',
                {
                    persistNotification: true,
                    notificationType: 'settings_update',
                },
            );
        } catch {
            toast.error('Failed to update phone call-out settings', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <SettingsPageSection
            title="Caregiver call-outs"
            description="Choose how caregivers can call out and when Phoebe may accept the request automatically."
        >
            <div className="space-y-6">
                <SettingsSection>
                    <div className="space-y-3 px-5 py-4">
                        <p className="text-foreground flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[15px] leading-7 font-medium">
                            <span>If the shift starts within</span>
                            <Input
                                aria-label="Look-ahead hours"
                                type="number"
                                min={0}
                                step="any"
                                className="h-8 w-20 text-center"
                                value={hoursText}
                                disabled={isUpdating}
                                onChange={(event) => {
                                    setHoursText(event.target.value);
                                    setDirty(true);
                                }}
                            />
                            <span>
                                hours, a scheduler reviews the request before it
                                takes effect.
                            </span>
                        </p>
                        <p className="text-muted-foreground text-sm">
                            Requests further out are accepted automatically only
                            during the windows below.
                            {rules.length === 0
                                ? ' With no acceptance windows, only the look-ahead threshold applies.'
                                : null}
                        </p>
                        <WeeklyScheduleRulesEditor
                            rules={rules}
                            onChange={(nextRules) => {
                                setRules(
                                    nextRules.filter(
                                        (
                                            rule,
                                        ): rule is WeeklyScheduleRuleFormValue =>
                                            'daysOfWeek' in rule,
                                    ),
                                );
                                setDirty(true);
                            }}
                            createDefaultRule={createDefaultWeeklyScheduleRule}
                            allowMultipleRules={true}
                            allowRemovingLastRule={true}
                            addRuleLabel="Add acceptance window"
                            ruleTestIdPrefix="cancellation-window"
                        />
                    </div>
                    {dirty ? (
                        <div className="flex justify-end px-5 py-4">
                            <Button
                                type="button"
                                disabled={isUpdating || hasInvalidWindow}
                                onClick={() => void savePolicy()}
                            >
                                Save cancellation policy
                            </Button>
                        </div>
                    ) : null}
                </SettingsSection>
                <p className="px-1 text-sm text-amber-700 dark:text-amber-400">
                    When cancellations by text or phone are on, Phoebe writes
                    each accepted call-out back to your EHR: it removes the
                    caregiver from the shift and reopens it there.
                </p>
                <SettingsSection title="SMS">
                    <SettingsRow
                        htmlFor="callouts-sms-enabled"
                        title="Accept cancellations by text"
                        description="Caregivers can send Phoebe a text to cancel an upcoming shift."
                        control={
                            <Switch
                                id="callouts-sms-enabled"
                                checked={smsEnabled}
                                disabled={isUpdating}
                                onCheckedChange={(checked) =>
                                    void setChannels('sms', checked)
                                }
                            />
                        }
                    />
                    <div className="px-5 py-4">
                        <SmsCancellationReplyCard
                            organizationId={organizationId}
                            reply={
                                organization?.shift_confirmation_settings
                                    ?.sms_shift_cancellation_disabled_reply ??
                                null
                            }
                            defaultReply={
                                organization?.shift_confirmation_settings
                                    ?.sms_shift_cancellation_disabled_reply_default ??
                                ''
                            }
                            isLoading={organization === undefined}
                            onSaved={async () => {
                                await mutateOrganization();
                            }}
                        />
                    </div>
                </SettingsSection>
                <SettingsSection title="Phone call">
                    <SettingsRow
                        htmlFor="callouts-voice-enabled"
                        title="Accept cancellations by phone"
                        description="Caregivers can call Phoebe to cancel an upcoming shift."
                        control={
                            <Switch
                                id="callouts-voice-enabled"
                                checked={voiceEnabled}
                                disabled={isUpdating}
                                onCheckedChange={(checked) =>
                                    void setChannels('voice', checked)
                                }
                            />
                        }
                    />
                    <SettingsRow
                        htmlFor="callouts-voice-always-transfer"
                        title="Connect phone call-outs to a scheduler"
                        description="When enabled, Phoebe captures the call-out and then connects the caregiver to the office."
                        control={
                            <Switch
                                id="callouts-voice-always-transfer"
                                checked={alwaysTransferPhoneCallouts}
                                disabled={isUpdating}
                                onCheckedChange={(checked) =>
                                    void saveAlwaysTransfer(checked)
                                }
                            />
                        }
                    />
                </SettingsSection>
            </div>
            <p className="text-muted-foreground px-1 text-sm">
                Accepted call-outs are recorded on the shift, and what Phoebe
                does next is set under{' '}
                <Link to="/settings/phoebe" className="underline">
                    Phoebe
                </Link>
                . Phone call-outs can also vary by answer schedule on the{' '}
                <Link to="/settings/phone" className="underline">
                    Call handling
                </Link>{' '}
                page.
            </p>
        </SettingsPageSection>
    );
}

function AgencyCancellationNoticeSection({
    organizationId,
}: {
    readonly organizationId: string;
}) {
    const { data: templates, mutate: mutateTemplates } = useCompletionTemplates(
        organizationId,
        { reason: 'settings-cancellations-notice' },
    );
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings-cancellations-notice-organization',
    });
    const { data: variableManifest } = useTemplateVariableManifest(
        organizationId,
        { reason: 'settings-cancellations-notice-variables' },
    );
    // Completion notices share the completion SMS header variable flow, same
    // as the classic-settings Messages card.
    const templateVariables = useMemo(
        () =>
            getTemplateVariablesForFlow(
                variableManifest,
                'completion_sms_header',
            ),
        [variableManifest],
    );
    const savedTemplate = useMemo(
        () =>
            templates?.find((t) => t.type === 'completion_cancelled')
                ?.template ?? DEFAULT_COMPLETION_CANCELLED_TEMPLATE,
        [templates],
    );
    const [draft, setDraft] = useState(savedTemplate);
    const [saveStatus, setSaveStatus] = useState<ButtonStatus>('idle');
    const isSaving = saveStatus === 'pending';
    useEffect(() => {
        setDraft(savedTemplate);
    }, [savedTemplate]);

    const hasChanges = draft.trim() !== savedTemplate;

    const handleSave = useCallback(async () => {
        setSaveStatus('pending');
        try {
            await getApiClient().put({
                url: '/organizations/{organization_id}/templates',
                pathParams: { organization_id: organizationId },
                body: {
                    templates: [
                        {
                            type: 'completion_cancelled',
                            template: draft.trim(),
                        },
                    ],
                },
            });
            await mutateTemplates();
            setSaveStatus('success');
            toast.success('Cancellation notice saved', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        } catch {
            setSaveStatus('error');
            toast.error('Failed to save cancellation notice', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        }
    }, [organizationId, draft, mutateTemplates]);

    return (
        <SettingsPageSection
            title="When you cancel shifts"
            description="The text Phoebe sends caregivers when you cancel the shifts from an open-shift outreach."
        >
            <SettingsPanel>
                <div className="space-y-3">
                    <AvailableVariables
                        variables={templateVariables}
                        label="Drag these variables"
                    />
                    <VariableTextEditor
                        value={draft}
                        onChange={setDraft}
                        variables={templateVariables}
                        variablePreviewValues={getTemplateVariablePreviewValuesForOrganization(
                            organization,
                        )}
                        ariaLabel="Cancellation notice template"
                        placeholder={DEFAULT_COMPLETION_CANCELLED_TEMPLATE}
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            onClick={handleSave}
                            status={saveStatus}
                            disabled={!hasChanges}
                        >
                            {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDraft(savedTemplate)}
                            disabled={!hasChanges || isSaving}
                        >
                            Discard Changes
                        </Button>
                    </div>
                </div>
            </SettingsPanel>
        </SettingsPageSection>
    );
}
